// Pass 2.12 — layout state owner.
//
// Pixel-native widths for the two resizable panes (sidebar +
// thread drawer), persisted to localStorage with a 150ms-debounced
// write. Reads on mount with defensive parsing — bad shape /
// malformed JSON / out-of-range values all clamp to defaults
// rather than throw.
//
// Pass 2.11 left an orphan key in some users' localStorage with
// values that may already match the new bounds; the read-and-clamp
// path consumes those silently. No separate cleanup pass needed.

import { useCallback, useEffect, useRef, useState } from "react";

export interface LayoutState {
  sidebarWidth: number;
  threadDrawerWidth: number;
}

export const DEFAULTS: LayoutState = {
  sidebarWidth: 288,
  threadDrawerWidth: 420,
};

export const SIDEBAR_BOUNDS = { min: 180, max: 400 };
export const THREAD_BOUNDS = { min: 280, max: 560 };

const STORAGE_KEY = "slack-archive-viewer:layout";
const WRITE_DEBOUNCE_MS = 150;

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Parse arbitrary unknown into a valid LayoutState. Returns null
 * on shape mismatch (caller falls back to DEFAULTS); clamps
 * in-range values to the bounds. Used both on read-from-storage
 * and on every set so a misbehaving caller can't store an
 * out-of-range value.
 */
function parseLayout(raw: unknown): LayoutState | null {
  if (typeof raw !== "object" || raw === null) return null;
  const sw = (raw as { sidebarWidth?: unknown }).sidebarWidth;
  const tw = (raw as { threadDrawerWidth?: unknown }).threadDrawerWidth;
  if (typeof sw !== "number" || typeof tw !== "number") return null;
  if (!Number.isFinite(sw) || !Number.isFinite(tw)) return null;
  return {
    sidebarWidth: clamp(sw, SIDEBAR_BOUNDS.min, SIDEBAR_BOUNDS.max),
    threadDrawerWidth: clamp(tw, THREAD_BOUNDS.min, THREAD_BOUNDS.max),
  };
}

/**
 * Read the persisted LayoutState. Returns DEFAULTS for any
 * recoverable failure (no storage, malformed JSON, wrong shape,
 * NaN). In-range values pass through unchanged; out-of-range
 * values get clamped.
 */
export function readStoredLayout(): LayoutState {
  if (typeof localStorage === "undefined") return DEFAULTS;
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return DEFAULTS;
  }
  if (raw === null) return DEFAULTS;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULTS;
  }
  return parseLayout(parsed) ?? DEFAULTS;
}

function writeStoredLayout(layout: LayoutState): void {
  if (typeof localStorage === "undefined") return;
  const sanitized = parseLayout(layout) ?? DEFAULTS;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
  } catch {
    // private mode / quota — silent.
  }
}

function clearStoredLayout(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore.
  }
}

export interface UseLayoutStateResult {
  sidebarWidth: number;
  threadDrawerWidth: number;
  setSidebarWidth: (width: number) => void;
  setThreadDrawerWidth: (width: number) => void;
  resetLayout: () => void;
}

export function useLayoutState(): UseLayoutStateResult {
  const [state, setState] = useState<LayoutState>(() => readStoredLayout());

  // Debounce timer ref so rapid drag ticks don't flood storage.
  // The lib's onLayout for v2 fires at 60+ Hz; even a hand-rolled
  // pointermove handler delivers at the same cadence. Collapsing
  // a drag into a single trailing write at the 150ms tail keeps
  // the storage event in other tabs from going wild.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<LayoutState | null>(null);

  const scheduleWrite = useCallback((next: LayoutState) => {
    pendingRef.current = next;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (pendingRef.current !== null) writeStoredLayout(pendingRef.current);
      timerRef.current = null;
      pendingRef.current = null;
    }, WRITE_DEBOUNCE_MS);
  }, []);

  // Flush any pending write on unmount so a debounce-in-flight
  // doesn't lose the user's last action when they navigate away.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        if (pendingRef.current !== null) writeStoredLayout(pendingRef.current);
        timerRef.current = null;
        pendingRef.current = null;
      }
    };
  }, []);

  const setSidebarWidth = useCallback(
    (width: number) => {
      const clamped = clamp(width, SIDEBAR_BOUNDS.min, SIDEBAR_BOUNDS.max);
      setState((prev) => {
        if (prev.sidebarWidth === clamped) return prev;
        const next = { ...prev, sidebarWidth: clamped };
        scheduleWrite(next);
        return next;
      });
    },
    [scheduleWrite],
  );

  const setThreadDrawerWidth = useCallback(
    (width: number) => {
      const clamped = clamp(width, THREAD_BOUNDS.min, THREAD_BOUNDS.max);
      setState((prev) => {
        if (prev.threadDrawerWidth === clamped) return prev;
        const next = { ...prev, threadDrawerWidth: clamped };
        scheduleWrite(next);
        return next;
      });
    },
    [scheduleWrite],
  );

  const resetLayout = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      pendingRef.current = null;
    }
    clearStoredLayout();
    setState(DEFAULTS);
  }, []);

  return {
    sidebarWidth: state.sidebarWidth,
    threadDrawerWidth: state.threadDrawerWidth,
    setSidebarWidth,
    setThreadDrawerWidth,
    resetLayout,
  };
}
